<script setup>
import { useStore } from "vuex";
import { useLoading } from "vue-loading-overlay";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import moment from "moment";
import { errorNotify, successNotify } from "@/mixins";
import DataTable from "@/components/panels/DataTable.vue";
import RowActions from "@/components/panels/RowActions.vue";
import FormModal from "@/components/modals/FormModal.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import PaginationBar from "@/components/layouts/PaginationBar.vue";

const store = useStore();
const $loading = useLoading({});

const controlUnitList = computed(() => store.getters['control_unit/controlUnitList']);
const units = computed(() => controlUnitList.value?.results || []);
const organizationList = computed(() => store.getters['organization/organizationList']);
const organizations = computed(() => organizationList.value?.results || []);
const stats = computed(() => store.getters['statistic/controlUnitsStatistic'] || {});

const INPUT_TYPES = ['ble', 'gm72', 'nlv52', 'nft', 'screen'];
const INPUT_LABEL = { ble: 'BLE', gm72: 'GM72', nlv52: 'NLV52', nft: 'NFT', screen: 'SCREEN' };
const INPUT_COLOR = {
  ble: 'var(--lb-blue)', gm72: 'var(--lb-success)', nlv52: 'var(--lb-warning)', nft: 'var(--lb-slate)', screen: 'var(--lb-red)',
};
const BOARD_TYPES = ['pro', 'classic', 'light'];
const BOARD_LABEL = { pro: 'Pro', classic: 'Classic', light: 'Light' };
const boardLabel = (b) => BOARD_LABEL[b] || b || '—';

// `is_provisioning` flips to true on the unit's first sync, so true means the
// unit is ready and false means it hasn't come online yet (same rule as
// statistic/control_units/ on the backend).
const STATUS_META = {
  provisioning: { cls: 'yes', icon: 'progress-clock', label: 'Provisioning' },
  ready: { cls: 'no', icon: 'check-circle-outline', label: 'Ready' },
};
const statusOf = (u) => STATUS_META[u.is_provisioning ? 'ready' : 'provisioning'];

// Every filter is server-side: control_unit/ takes `sn` (icontains),
// `organization` (id of the installed device's org), `input_type`,
// `board_type` and `is_provisioning`, and paginates the result.
const PAGE_SIZE = 10;
const page = ref(1);
const searchSn = ref('');
const inputType = ref('');
const organization = ref('');
const boardType = ref('');
const provisioning = ref(''); // '' | 'true' (ready) | 'false' (provisioning)

const totalPages = computed(() => controlUnitList.value?.total_pages || 1);
const totalCount = computed(() => controlUnitList.value?.count || 0);

function loadControlUnits() {
  const params = new URLSearchParams({ page: page.value, page_size: PAGE_SIZE });
  if (searchSn.value.trim()) params.set('sn', searchSn.value.trim());
  if (organization.value) params.set('organization', organization.value);
  if (inputType.value) params.set('input_type', inputType.value);
  if (boardType.value) params.set('board_type', boardType.value);
  if (provisioning.value) params.set('is_provisioning', provisioning.value);
  const loader = $loading.show({});
  return store.dispatch('control_unit/getControlUnitList', `?${params}`)
    .finally(() => loader.hide());
}

function applyFilters() {
  page.value = 1;
  loadControlUnits();
}

watch([organization, inputType, boardType, provisioning], applyFilters);

let searchTimer = null;
watch(searchSn, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(applyFilters, 300);
});
onBeforeUnmount(() => clearTimeout(searchTimer));

function setPage(p) {
  page.value = p;
  loadControlUnits();
}

function loadStats() {
  return store.dispatch('statistic/getControlUnitsStatistic').then(() => {
    const err = store.getters['statistic/controlUnitsStatisticError'];
    if (err) errorNotify(err);
  });
}

function reload() {
  loadControlUnits();
  loadStats();
}

onMounted(() => {
  reload();
  store.dispatch('organization/getOrganizationList', '?page_size=1000');
});

const fmt = (ts) => (ts ? moment(ts).format('MMM D, YYYY') : '—');

// KPI block comes from statistic/control_units/ — fleet-wide, it doesn't take
// the organization filter.
const unitMetrics = computed(() => [
  { label: 'Provisioning', value: stats.value.provisioning ?? 0, icon: 'progress-clock', color: 'var(--lb-warning)' },
  { label: 'Ready', value: stats.value.ready ?? 0, icon: 'check-circle-outline', color: 'var(--lb-success)' },
  { label: 'Pro', value: stats.value.board_types?.pro ?? 0, icon: 'lightning-bolt-outline', color: 'var(--lb-blue)' },
  { label: 'Classic', value: stats.value.board_types?.classic ?? 0, icon: 'archive-outline', color: 'var(--lb-slate)' },
]);
const typeStats = computed(() => INPUT_TYPES.map((t) => ({
  label: INPUT_LABEL[t], v: stats.value.input_types?.[t] ?? 0, color: INPUT_COLOR[t],
})));
const typeStatsMax = computed(() => Math.max(...typeStats.value.map((d) => d.v), 1));

// ---- create / edit ----
const modal = ref(null); // { mode, data }
const submitting = ref(false);

const unitFields = computed(() => {
  const snField = modal.value?.mode === 'edit'
    ? { key: 'sn', label: 'Serial number', readonly: true }
    : { key: 'sn', label: 'Serial number', placeholder: 'Leave blank to auto-generate', optional: true };
  return [
    { ...snField, full: true },
    {
      key: 'input_type', label: 'Input type', type: 'select', required: true, placeholder: 'Select input type',
      options: INPUT_TYPES.map((t) => ({ value: t, label: INPUT_LABEL[t] })),
    },
    { key: 'screen', label: 'Screen', optional: true, placeholder: 'Screen config' },
  ];
});

function openModal(mode, unit) {
  modal.value = {
    mode,
    data: mode === 'edit'
      ? { sn: unit.sn, input_type: unit.input_type, screen: unit.screen || '' }
      : {},
  };
}

function saveUnit(vals) {
  submitting.value = true;
  const data = { input_type: vals.input_type, screen: vals.screen || '' };
  let req;
  if (modal.value.mode === 'add') {
    if (vals.sn) data.sn = vals.sn;
    req = store.dispatch('control_unit/createControlUnit', data);
  } else {
    req = store.dispatch('control_unit/updateControlUnit', { id: modal.value.data.sn, data });
  }
  req
    .then(() => {
      const err = modal.value.mode === 'add'
        ? store.getters['control_unit/createControlUnitError']
        : store.getters['control_unit/updateControlUnitError'];
      if (err) {
        errorNotify(err);
      } else {
        successNotify(modal.value.mode === 'add' ? 'Control unit added' : 'Control unit updated');
        modal.value = null;
        reload();
      }
    })
    .finally(() => { submitting.value = false; });
}

// ---- delete ----
const del = ref(null);
function confirmDelete() {
  store.dispatch('control_unit/deleteControlUnit', del.value.sn)
    .then(() => {
      const err = store.getters['control_unit/deleteControlUnitError'];
      if (err) errorNotify(err);
      else successNotify('Control unit deleted');
      del.value = null;
      reload();
    });
}

const cols = 'minmax(160px,1.1fr) minmax(140px,1fr) minmax(130px,1fr) 100px 90px 100px 120px 130px';
const columns = ['S/N', 'Locker', 'Organization', 'Input type', 'Board', 'Firmware', 'Created', 'Status'];
</script>

<template>
  <Teleport to="#topbar-actions">
    <button class="btn-primary" type="button" @click="openModal('add')">
      <span class="mdi mdi-plus"></span>
      Add Control Unit
    </button>
  </Teleport>

  <div class="ro-top">
    <div class="metric-cluster">
      <div class="mc-total">
        <span class="mc-total__wm mdi mdi-memory"></span>
        <div class="mc-total__title">Total Units</div>
        <div class="mc-total__num">{{ stats.total ?? 0 }}</div>
      </div>
      <div v-for="m in unitMetrics" :key="m.label" class="mcard" :class="{ 'is-zero': m.value === 0 }">
        <div class="mcard__l">
          <div class="mcard__ico"><span class="mdi" :class="`mdi-${m.icon}`" :style="{ color: m.color }"></span></div>
          <div class="mcard__label">{{ m.label }}</div>
        </div>
        <div class="mcard__num">{{ m.value }}</div>
      </div>
    </div>

    <div class="ro-right">
      <div class="ro-card avgcard">
        <span class="avgcard__wm mdi mdi-check-decagram-outline"></span>
        <div class="avgcard__title">Installed</div>
        <div class="avgcard__num">{{ stats.installed ?? 0 }}</div>
        <div class="avgcard__foot">Units mounted in a locker · of {{ stats.total ?? 0 }} total</div>
      </div>
      <div class="ro-card">
        <div class="ro-card__head">
          <span class="mdi mdi-chart-bar"></span>
          <h3>Units by Input Type</h3>
          <span class="sub">{{ stats.total ?? 0 }} total</span>
        </div>
        <div class="weekchart">
          <div v-for="d in typeStats" :key="d.label" class="weekbar">
            <div class="weekbar__val">{{ d.v }}</div>
            <div class="weekbar__track">
              <div class="weekbar__fill" :style="{ height: `${(d.v / typeStatsMax) * 100}%`, background: d.color }"></div>
            </div>
            <div class="weekbar__day">{{ d.label }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="toolbar">
    <div class="field-search">
      <span class="mdi mdi-magnify"></span>
      <input v-model="searchSn" placeholder="Search by S/N…">
    </div>
    <div class="field-select">
      <span class="mdi mdi-office-building-outline lead"></span>
      <select v-model="organization">
        <option value="">All organizations</option>
        <option v-for="o in organizations" :key="o.id" :value="String(o.id)">{{ o.name }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div class="field-select">
      <span class="mdi mdi-tag-outline lead"></span>
      <select v-model="inputType">
        <option value="">All input types</option>
        <option v-for="t in INPUT_TYPES" :key="t" :value="t">{{ INPUT_LABEL[t] }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div class="field-select">
      <span class="mdi mdi-developer-board lead"></span>
      <select v-model="boardType">
        <option value="">All boards</option>
        <option v-for="b in BOARD_TYPES" :key="b" :value="b">{{ BOARD_LABEL[b] }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div class="field-select">
      <span class="mdi mdi-progress-check lead"></span>
      <select v-model="provisioning">
        <option value="">All statuses</option>
        <option value="true">Ready</option>
        <option value="false">Provisioning</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div class="spacer"></div>
    <span class="count">{{ totalCount }} of {{ stats.total ?? 0 }} units</span>
  </div>

  <DataTable :cols="cols" :columns="columns" :rows="units" row-key="sn" empty-text="No control units match your filters.">
    <template #row="{ row }">
      <div class="td">
        <div class="td-name">
          <div class="ic"><span class="mdi mdi-memory"></span></div>
          <div class="nm"><b class="mono">{{ row.sn }}</b></div>
        </div>
      </div>
      <div class="td"><span :class="{ muted: !row.device }">{{ row.device || 'Not installed' }}</span></div>
      <div class="td"><span>{{ row.organization || '—' }}</span></div>
      <div class="td">
        <span class="inputtype" :class="row.input_type">{{ INPUT_LABEL[row.input_type] || row.input_type || '—' }}</span>
      </div>
      <div class="td">
        <span v-if="row.board_type" class="boardtype" :class="row.board_type">{{ boardLabel(row.board_type) }}</span>
        <span v-else class="muted">—</span>
      </div>
      <div class="td"><span :class="row.firmware_version ? 'mono' : 'muted'">{{ row.firmware_version || '—' }}</span></div>
      <div class="td"><span class="muted">{{ fmt(row.created_at) }}</span></div>
      <div class="td">
        <span class="provtag" :class="statusOf(row).cls">
          <span class="mdi" :class="`mdi-${statusOf(row).icon}`"></span>
          {{ statusOf(row).label }}
        </span>
      </div>
      <RowActions
        :copies="[{ icon: 'content-copy', label: 'Copy S/N', text: row.sn }]"
        detail-label="Edit"
        deletable
        @detail="openModal('edit', row)"
        @delete="del = row" />
    </template>
  </DataTable>

  <PaginationBar
    :page="page"
    :total-pages="totalPages"
    :count="totalCount"
    @update:page="setPage" />

  <FormModal
    :open="!!modal"
    :title="modal?.mode === 'edit' ? 'Edit Control Unit' : 'Add Control Unit'"
    :fields="unitFields"
    :initial="modal?.data || {}"
    :submit-label="modal?.mode === 'edit' ? 'Save changes' : 'Add unit'"
    :submitting="submitting"
    @submit="saveUnit"
    @cancel="modal = null" />

  <ConfirmModal
    :open="!!del"
    danger
    title="Delete control unit?"
    :message="del ? `“${del.sn}” will be permanently removed.` : ''"
    confirm-label="Delete"
    @confirm="confirmDelete"
    @cancel="del = null" />
</template>
